import { z } from 'zod';
import { yesNoValues } from 'src/apis/storage/mappings';
import type { StorageLocationWriteData } from 'src/apis/storage/types';
import { networkPointOptionSchema } from 'src/components/form/select/network-point-select/network-point-multi-select';
import { searchableSingleSelectSchema } from 'src/components/form/select/searchable-single-select';
import {
  preprocessAsArray,
  requiredOutput,
} from 'src/components/form/zod-utilities';
import { urlRegex } from 'src/utils/validation';

const marketAreaEntrySchema = z.object({
  marketAreaId: searchableSingleSelectSchema,
  injectionPoints: z.preprocess(
    preprocessAsArray,
    z.array(networkPointOptionSchema).default([])
  ),
  withdrawalPoints: z.preprocess(
    preprocessAsArray,
    z.array(networkPointOptionSchema).default([])
  ),
});

export type CreateStorageLocationFormValues = {
  locationName: string | null;
  locationEic: string | null;
  marketAreas: z.input<typeof marketAreaEntrySchema>[];
  virtualPoint: boolean | null;
  comment: string | null;
  link: string | null;
};

const eicRegExp = /^[A-Z0-9]{2}[V-Z][A-Z-0-9]{13}$/;
const invalidEicMessage =
  'The EIC must be exactly 16 characters and match the EIC format (e.g. 21Z000000000004A).';

const createStorageLocationSchema = z.object({
  locationName: z
    .string()
    .max(255, 'Please enter a {label} that is shorter than 255 characters.')
    .nullable()
    .transform(requiredOutput()),
  locationEic: z
    .string()
    .regex(eicRegExp, invalidEicMessage)
    .nullable()
    .transform(requiredOutput()),
  marketAreas: z
    .array(marketAreaEntrySchema)
    .min(1, 'Please add at least one Market Area.'),
  virtualPoint: z
    .enum(yesNoValues)
    .default('false')
    .transform((v) => v === 'true'),
  comment: z.string().nullable().optional(),
  link: z
    .string()
    .regex(
      urlRegex,
      'URL format is incorrect. Please enter a valid URL which starts with https://'
    )
    .nullable(),
});

export type ValidCreateStorageLocationFormValues = z.output<
  typeof createStorageLocationSchema
>;

export function getCreateStorageLocationSchema() {
  return createStorageLocationSchema;
}

export function formValuesToCreateRequest(
  values: ValidCreateStorageLocationFormValues
): StorageLocationWriteData {
  return {
    locationName: values.locationName,
    locationEic: values.locationEic,
    ...(values.comment && { comment: values.comment }),
    ...(values.link && { link: values.link }),
    marketAreas: values.marketAreas.map((entry) => ({
      marketAreaId: entry.marketAreaId.value,
      injectionPoints: entry.injectionPoints.map((o) => o.value),
      withdrawalPoints: entry.withdrawalPoints.map((o) => o.value),
    })),
    virtualPoint: values.virtualPoint,
  };
}
